/**
 * Trip Type and CSR Status Helpers
 *
 * This module provides utilities for:
 * - Deriving trip type from tenant data (Transient, Base Tenant)
 * - CSR status colors and labels for UI display
 *
 * Note: VIP tags (YC, VIP, etc.) are displayed separately from trip type.
 */

import { CsrStatus } from "../types";

// =============================================================================
// Trip Type (derived from tenant)
// =============================================================================

export type TripType = "transient" | "base";

/**
 * Derive trip type from tenant data
 * @param tenantType - The tenant's type ("base" or "transient")
 */
export const getTripType = (
  tenantType: string | undefined
): TripType => {
  return tenantType === "base" ? "base" : "transient";
};

/**
 * Colors for trip type badges (using theme-aligned colors)
 */
export const tripTypeColors: Record<TripType, string> = {
  transient: "#6b7280", // Gray - neutral for transient
  base: "#23967F", // Secondary teal - matches theme
};

/**
 * Colors for tenant type dropdowns (matching aircraft nose grabber circles)
 * Used in Add Aircraft, Create Trip, and Add Trip dialogs
 */
export const tenantTypeDropdownColors: Record<string, string> = {
  transient: "#3c6ce9", // Blue (from frontWheels CSS)
  base: "#23967F", // Teal (from frontWheels.tenant CSS)
};

/**
 * Labels for trip type display
 */
export const tripTypeLabels: Record<TripType, string> = {
  transient: "Transient",
  base: "Base Tenant",
};

/**
 * Short labels for compact badge display
 */
export const tripTypeShortLabels: Record<TripType, string> = {
  transient: "TRANSIENT",
  base: "BASE",
};

// =============================================================================
// CSR Status (customer-facing workflow)
// =============================================================================

/**
 * CSR Status colors for UI display (using theme colors)
 * Each status has background, border, and text colors
 */
export const csrStatusColors: Record<
  CsrStatus,
  { bg: string; border: string; text: string }
> = {
  requested: {
    bg: "rgba(220, 38, 38, 0.08)",
    border: "rgba(220, 38, 38, 0.25)",
    text: "#dc2626",
  }, // Red (urgent action needed)
  confirmed: {
    bg: "rgba(245, 194, 92, 0.12)",
    border: "rgba(245, 194, 92, 0.35)",
    text: "#d4a84b",
  }, // Yellow/gold (in progress)
  completed: {
    bg: "rgba(35, 150, 127, 0.08)",
    border: "rgba(35, 150, 127, 0.25)",
    text: "#23967F",
  }, // Teal (done)
  cancelled: {
    bg: "rgba(107, 114, 128, 0.08)",
    border: "rgba(107, 114, 128, 0.25)",
    text: "#6b7280",
  }, // Gray (inactive)
};

/**
 * CSR Status filter colors (solid colors for filter dropdowns, theme-aligned)
 */
export const csrStatusFilterColors: Record<CsrStatus, string> = {
  requested: "#dc2626", // Red (urgent action needed)
  confirmed: "#f5c25c", // Yellow/gold (in progress)
  completed: "#23967F", // Teal (done)
  cancelled: "#6b7280", // Gray (inactive)
};

/**
 * CSR Status labels for display
 */
export const csrStatusLabels: Record<CsrStatus, string> = {
  requested: "Requested",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
};

/**
 * All CSR status values in workflow order
 */
export const csrStatusValues: CsrStatus[] = [
  "requested",
  "confirmed",
  "completed",
  "cancelled",
];

/**
 * Get default CSR status for new trips
 */
export const getDefaultCsrStatus = (): CsrStatus => "requested";
